import {
  getDocuments,
  invalidateDocumentListData
} from "@prismicio/editor-fields"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import {
  invalidateFetcherData,
  useRequest
} from "@prismicio/editor-support/Suspense"
import {
  Box,
  Button,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogContent,
  DialogHeader,
  Text,
  Tooltip,
  useAlert,
  useToast
} from "@prismicio/editor-ui"
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { z } from "zod"

import { staticEditorConfig } from "../../app/editorConfig"
import {
  getReleasePermissions,
  getReleaseSizeQueryOptions,
  type Release,
  updateReleasesListData
} from "../../app/services/release"
import { useBulkAction } from "../BulkActions/BulkActionsProvider"

interface ReleasePublishButtonProps {
  release: Release
  size?: "small" | "medium" | "large"
  color?: "purple" | "grey"
  buttonLabel?: string
}

export function ReleasePublishButton(props: ReleasePublishButtonProps) {
  const {
    release,
    size = "medium",
    color = "purple",
    buttonLabel = "Publish"
  } = props
  const [dialogOpen, setDialogOpen] = useState(false)
  const { queuedTasks } = useBulkAction()

  const isLoading =
    release.migration &&
    queuedTasks.some(
      (task) => task.releaseId === release.id && task.action === "publish"
    )

  // TODO: Delay request based on https://linear.app/prismic/issue/PBD-1438/delay-request-by-x-amount-of-time

  const { disabled, tooltip } = usePublishReleaseButton(release)

  return (
    <>
      <Tooltip
        visible={tooltip.visible}
        title={tooltip.title}
        content={tooltip.content}
      >
        <Button
          size={size}
          startIcon="arrowUpward"
          color={color}
          onClick={() => setDialogOpen(true)}
          disabled={disabled}
          loading={isLoading}
        >
          {buttonLabel}
        </Button>
      </Tooltip>
      <ReleasePublishConfirmationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        release={release}
      />
    </>
  )
}

function usePublishReleaseButton(release: Release) {
  const { canPublish: canPublishRelease } = useRequest(getReleasePermissions, [
    release.id
  ])

  const { data } = useSuspenseQuery(getReleaseSizeQueryOptions(release.id))
  const releaseSize = data.documentsCount

  const isReleaseEmpty = releaseSize === 0

  return {
    disabled: isReleaseEmpty || !canPublishRelease,
    tooltip: getTooltip({ isReleaseEmpty, canPublishRelease })
  }
}

interface GetTooltipArgs {
  isReleaseEmpty: boolean
  canPublishRelease: boolean
}

function getTooltip(args: GetTooltipArgs) {
  const { isReleaseEmpty, canPublishRelease } = args

  if (!canPublishRelease) {
    return {
      visible: true,
      title: "You can't publish this release",
      content:
        "You are missing publishing rights for some of the documents in the release."
    }
  }

  if (isReleaseEmpty) {
    return {
      visible: true,
      title: "You can't publish an empty release",
      content: "Add some documents first."
    }
  }

  return {
    visible: false,
    title: "",
    content: ""
  }
}

const migrationReleasePublishResponseSchema = z.object({
  data: z.object({
    taskId: z.string(),
    releaseId: z.string()
  }),
  totalItems: z.number().int()
})

interface ReleasePublishConfirmationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  release: Release
}

function ReleasePublishConfirmationDialog(
  props: ReleasePublishConfirmationDialogProps
) {
  const { open, onOpenChange, release } = props
  const { addOnGoingTask } = useBulkAction()

  const [loading, setLoading] = useState(false)
  const { showAlert } = useAlert()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const releaseLabel = release.label || "..."

  async function onPublishRelease() {
    setLoading(true)

    try {
      const data = await publishRelease({ releaseId: release.id })

      if (release.migration) {
        // Note: if migration release, add task to BulkActions handler
        const result = migrationReleasePublishResponseSchema.parse(data)
        addOnGoingTask(result.data.taskId)

        setLoading(false)
        onOpenChange(false)
        return
      }

      updateReleasesListData((releases) => {
        // remove the release from the releases cache
        return releases.filter((_release) => _release.id !== release.id)
      })
      // Publishing a release will result in document status being updated
      invalidateFetcherData(getDocuments)
      void invalidateDocumentListData(queryClient)
      showToast({
        title: "Release has been published"
      })

      navigate("/upcoming", {
        unstable_flushSync: true
      })
    } catch {
      setLoading(false)
      onOpenChange(false)
      showAlert({
        title: "Error",
        subtitle: `Something went wrong when publishing ${releaseLabel}.`
      })
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open} size="small">
      <DialogHeader
        icon="alert"
        title="Do you really want to publish this release?"
      />

      <DialogContent>
        <Box padding={16}>
          <Text>You are about to publish the release: {releaseLabel}.</Text>
        </Box>
      </DialogContent>

      <DialogActions>
        <DialogCancelButton>No</DialogCancelButton>
        <DialogActionButton
          onClick={onPublishRelease}
          color="purple"
          loading={loading}
        >
          Yes, I want to publish the release now
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

interface PublishReleaseArgs {
  releaseId: string
}

function publishRelease(args: PublishReleaseArgs) {
  const { releaseId } = args
  const { authStrategy, coreApiBaseUrl } = staticEditorConfig

  return request(new URL(`releases/${releaseId}/publish`, coreApiBaseUrl), {
    method: "POST",
    credentials: getRequestCredentials(authStrategy),
    okType: z.unknown()
  })
}
